import type React from 'react';
import { forwardRef } from 'react';
import { SettingsRow } from 'tradera/apps/my-tradera/app/components/settings/components/settings-row/settings-row';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger
} from 'tradera/components/dropdown-menu2/dropdown-menu2';
import { Button } from 'tradera/components/atoms/button/button';
import { DropdownMenuItemSelectable } from 'tradera/apps/syi/components/dropdown-field';
import { Text } from 'tradera/components/atoms/text/text';

type Props = {
    selected?: DropdownOption;
    options?: DropdownOption[];
    label?: string;
    labelComponent?: React.ReactNode;
    name: string;
    onChange?: (option: DropdownOption) => void;
    subLabel?: string;
    tooltip?: React.ReactNode;
    customDropdownContent?: React.ReactNode;
    size?: string;
    className?: string;
    dropdownTitle?: string;
};

export type DropdownOption = {
    value: string;
    text: string;
};

export const SettingsDropdown = forwardRef<HTMLDivElement, Props>(
    (
        {
            selected,
            options,
            label,
            labelComponent,
            name,
            onChange,
            subLabel,
            tooltip,
            customDropdownContent,
            size,
            className,
            dropdownTitle
        },
        ref
    ) => {
        return (
            <SettingsRow subLabel={subLabel} className={className} ref={ref}>
                <label
                    className="d-flex align-items-center flex-grow-1 mr-2 cursor-pointer py-1 mb-0"
                    htmlFor={name}>
                    <span className="font-hansen">
                        {label ?? labelComponent}
                    </span>
                    {tooltip && <div className="ml-1">{tooltip}</div>}
                </label>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild id={name}>
                        <Button
                            theme="link"
                            linkReverted
                            iconRight={
                                <ArrowDownIcon className="icon icon-xs" />
                            }
                            className="py-1">
                            {selected?.text}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        position="right"
                        size={size ?? '320px'}>
                        {dropdownTitle && (
                            <Text
                                font="hansen"
                                className="pt-3 pb-2 mx-3 border-bottom">
                                {dropdownTitle}
                            </Text>
                        )}
                        {options?.map((option) => (
                            <DropdownMenuItemSelectable
                                key={option.value}
                                selected={selected?.value === option.value}
                                onSelect={() => {
                                    if (selected?.value !== option.value) {
                                        onChange?.(option);
                                    }
                                }}
                                text={option.text}
                            />
                        ))}
                        {customDropdownContent}
                    </DropdownMenuContent>
                </DropdownMenu>
            </SettingsRow>
        );
    }
);

SettingsDropdown.displayName = 'SettingsDropdown';
